// Embedded, the shell owns the language selection and publishes it as bridge state: AO reads the
// current value once and then follows `localeChanged`.
//
// Goes through the context store's setLanguageLocale, which also re-seeds the catalogues and bumps
// `appKey` — the re-render vue-i18n 9 does not do on its own (AOV3-8139).
import { useContextStore } from '@/stores/context';
import { isEmbedded } from '@/webshell-embed/embedded';
import { shellBridge } from '@/webshell-embed/platform-bridge';
import { resolveLocale } from '@/webshell-embed/resolve-locale';

let unsubscribe = null;

function applyShellLocale(requested) {
  const contextStore = useContextStore();
  // setLanguageLocale does not validate its argument, and `currentLocale` stamps `x-current-locale`
  // on every request — so an unavailable tag would reach AO's server strings and exports. Resolve
  // against what is actually loaded first.
  const available = Object.keys(contextStore.i18n?.translations || {});
  const locale = resolveLocale(requested, available);
  if (!locale || locale === contextStore.currentLocale) return;
  contextStore.setLanguageLocale({ locale });
}

// Follow the shell's language selection. Call AFTER loadUserContext has resolved: setLanguageLocale
// reads `i18n.translations`, which the user context supplies, so an earlier call resolves against an
// empty list and silently does nothing. Idempotent; no-op when standalone.
export function subscribeToShellLocale() {
  if (unsubscribe || !isEmbedded()) return;
  const bridge = shellBridge();
  if (!bridge) return;

  // `subscribe` only fires on change, and the shell restores its stored locale long before AO mounts,
  // so the current value has to be applied explicitly.
  applyShellLocale(bridge.locale);
  unsubscribe = bridge.subscribe('localeChanged', applyShellLocale);
}

// Stop following when AO unmounts. The shell's locale store outlives AO's app instance, so without
// this a language switch made in the next product still drives AO's stores.
export function unsubscribeFromShellLocale() {
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
}
